/**
 * Geteilte DOM-Ausfüll-Logik für BAFA-Formulare im iframe (Antrag + Verwendungsnachweis).
 * Felder werden über sichtbaren Label-Text gefunden, nicht über IDs — die kennt man vor
 * dem Login nicht, das Label bleibt stabil.
 */

export interface FillField {
  label: string;
  value: string | null | undefined;
  /** 'radio' wählt unter mehreren <input type=radio> dasjenige, dessen eigenes Label
   *  dem Wert entspricht — statt Text in EIN Feld zu schreiben. `label` UND `value`
   *  sind hier der gesuchte Options-Text.
   *  'button' klickt einen <input type=button>/<button> ohne Formularwert (z.B. „Zeile
   *  hinzufügen" bei den De-minimis-Beihilfen) — `value` wird dafür ignoriert, `label`
   *  ist der gesuchte Button-Text (dessen value-Attribut bei <input type=button>).
   *  'checkbox' setzt EIN <input type=checkbox> auf checked (z.B. ESF-Grundsätze-Fragebogen,
   *  mehrere Checkboxen pro Frage möglich) — braucht immer `id`, da BAFA hier KEIN <label>
   *  rendert (wie bei den Radio-Gruppen ohne Label). */
  kind?: 'radio' | 'button' | 'checkbox';
  /** Adressiert das Zielelement direkt per DOM-id statt über sichtbaren Label-Text —
   *  für Felder, die BAFA ohne zugehöriges <label> rendert (z.B. manche Radio-Gruppen,
   *  die De-minimis-Tabellenzeile). Falls gesetzt, wird die Label-Suche übersprungen. */
  id?: string;
}

/** Nach einem Klick (Radio/Button), der abhängige Felder erst sichtbar/anlegt (z.B.
 *  Personentyp-Radio schaltet die Steuernummer frei, „Zeile hinzufügen" legt eine neue
 *  De-minimis-Zeile an), rendert BAFAs Formular-JS asynchron nach — ein sofortiger
 *  Zugriff im selben Tick findet das Zielelement noch nicht. Kurze Pause, bevor es
 *  mit den nächsten Feldern weitergeht. */
function waitForRender(ms = 120): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Sucht einen Klick-Button (kein Formularwert) über seinen sichtbaren Text — bei
 *  <input type=button> steht der im value-Attribut, bei <button> im textContent. */
function findButtonByLabel(doc: Document, label: string): HTMLElement | null {
  const target = normalizeLabel(label);
  const candidates = Array.from(doc.querySelectorAll('input[type=button], button'));
  const match = candidates.find((el) => {
    const text = el.tagName === 'BUTTON' ? el.textContent || '' : (el as HTMLInputElement).value || '';
    return normalizeLabel(text).includes(target);
  });
  return (match as HTMLElement) || null;
}

export function normalizeLabel(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9äöüß]+/g, ' ').trim();
}

/** Zerlegt einen vollen Namen in Vor- und Nachname — letztes Wort gilt als Nachname,
 *  der Rest als Vorname (deckt auch mehrteilige Vornamen ab). */
export function splitName(fullName?: string): { vorname: string; nachname: string } {
  const parts = (fullName || '').trim().split(' ').filter(Boolean);
  if (parts.length === 0) return { vorname: '', nachname: '' };
  if (parts.length === 1) return { vorname: '', nachname: parts[0] };
  return { vorname: parts.slice(0, -1).join(' '), nachname: parts[parts.length - 1] };
}

/** Setzt den Wert über den nativen Property-Setter statt `el.value = x` — React/Angular-
 *  Formulare im BAFA-Frame beobachten den Value-Setter selbst, ein simples Zuweisen wird
 *  von deren internem State-Tracking sonst nicht bemerkt (Feld sieht befüllt aus, ist es
 *  aber nicht). */
export function setNativeValue(el: HTMLInputElement | HTMLTextAreaElement, value: string) {
  const proto = el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  setter?.call(el, value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

/** Sucht das Eingabefeld zu einem <label> im BAFA-iframe. */
export function elementForLabel(doc: Document, l: HTMLLabelElement): HTMLElement | null {
  const forId = l.getAttribute('for');
  if (forId) {
    const el = doc.getElementById(forId);
    if (el) return el;
  }
  const inner = l.querySelector('input, select, textarea');
  if (inner) return inner as HTMLElement;
  const group = l.closest('.form-group, .row, div');
  const fallback = group?.querySelector('input, select, textarea');
  return (fallback as HTMLElement) || null;
}

export function matchLabel(labels: HTMLLabelElement[], label: string): HTMLLabelElement | undefined {
  const target = normalizeLabel(label);
  const targetTokens = target.split(' ').filter(Boolean);
  let match = labels.find((l) => normalizeLabel(l.textContent || '') === target);
  if (!match) match = labels.find((l) => normalizeLabel(l.textContent || '').startsWith(target));
  if (!match) match = labels.find((l) => normalizeLabel(l.textContent || '').includes(target));
  // Fallback: alle Wörter des gesuchten Labels kommen im echten Label vor, auch wenn
  // BAFA einen Einschub hat wie „Telefon (tagsüber)Vorwahl" — Substring-Suche allein
  // findet das nicht, weil „tagsüber" den zusammenhängenden Text unterbricht.
  if (!match) {
    match = labels.find((l) => {
      const tokens = new Set(normalizeLabel(l.textContent || '').split(' ').filter(Boolean));
      return targetTokens.length > 0 && targetTokens.every((t) => tokens.has(t));
    });
  }
  return match;
}

/** Textfelder/Selects — Radio/Checkbox-Labels bleiben außen vor (siehe findRadioByLabel),
 *  sonst kollidiert z.B. „BAFA-ID" (Radiofeld) mit dem gleichnamigen Textfeld „BAFA-ID: *".
 *  `exclude` schließt Zielelemente aus, die fillFields schon befüllt hat — sonst würden
 *  zwei Felder mit demselben Label (z.B. „Vorname" für Kunde UND Berater) beide auf das
 *  erste Treffer-Element zielen und sich lautlos gegenseitig überschreiben. */
export function findFieldByLabel(doc: Document, label: string, exclude?: Set<HTMLElement>): HTMLElement | null {
  const labels = Array.from(doc.querySelectorAll('label')).filter((l) => {
    const el = elementForLabel(doc, l);
    const type = (el as HTMLInputElement | null)?.type;
    if (type === 'radio' || type === 'checkbox') return false;
    if (exclude && el && exclude.has(el)) return false;
    return true;
  });
  const match = matchLabel(labels, label);
  return match ? elementForLabel(doc, match) : null;
}

/** Wie findFieldByLabel, aber ausschließlich unter <input type=radio> — der gesuchte
 *  Text ist hier die Options-Beschriftung selbst (z.B. "Ja", "juristische Person"). */
export function findRadioByLabel(doc: Document, label: string, exclude?: Set<HTMLElement>): HTMLInputElement | null {
  const labels = Array.from(doc.querySelectorAll('label')).filter((l) => {
    const el = elementForLabel(doc, l);
    if ((el as HTMLInputElement | null)?.type !== 'radio') return false;
    if (exclude && el && exclude.has(el)) return false;
    return true;
  });
  const match = matchLabel(labels, label);
  if (!match) return null;
  const el = elementForLabel(doc, match);
  return el && el.tagName === 'INPUT' ? (el as HTMLInputElement) : null;
}

/** Trägt alle `fields` ins Formular im übergebenen Dokument ein. Gibt die Labels der
 *  Felder zurück, die nicht gefunden wurden bzw. deren Wert vom Browser verworfen wurde
 *  (z.B. <input type=date> mit unpassendem Format) — der Aufrufer entscheidet, wie er
 *  das meldet (z.B. per alert). */
export async function fillFields(doc: Document, fields: FillField[]): Promise<string[]> {
  const missed: string[] = [];
  const used = new Set<HTMLElement>();
  for (const { label, value, kind, id } of fields) {
    if (kind === 'button') {
      const button = id ? (doc.getElementById(id) as HTMLElement | null) : findButtonByLabel(doc, label);
      if (!button) { missed.push(label); continue; }
      button.dispatchEvent(new Event('click', { bubbles: true }));
      await waitForRender();
      continue;
    }
    if (!value) continue;
    if (kind === 'radio') {
      const radio = id ? (doc.getElementById(id) as HTMLInputElement | null) : findRadioByLabel(doc, label, used);
      if (!radio) { missed.push(label); continue; }
      used.add(radio);
      radio.checked = true;
      radio.dispatchEvent(new Event('click', { bubbles: true }));
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      await waitForRender();
      continue;
    }
    if (kind === 'checkbox') {
      const checkbox = id ? (doc.getElementById(id) as HTMLInputElement | null) : null;
      if (!checkbox) { missed.push(label); continue; }
      used.add(checkbox);
      checkbox.checked = true;
      checkbox.dispatchEvent(new Event('click', { bubbles: true }));
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      await waitForRender();
      continue;
    }
    const target = id ? doc.getElementById(id) : findFieldByLabel(doc, label, used);
    if (!target) { missed.push(label); continue; }
    used.add(target);
    // tagName statt instanceof — target gehört zum iframe-Realm, dessen HTMLSelectElement
    // ist nicht dasselbe wie das dieses Fensters (Cross-Realm-instanceof liefert sonst
    // immer false, selbst wenn target korrekt gefunden wurde).
    const tag = target.tagName;
    if (tag === 'SELECT') {
      const select = target as HTMLSelectElement;
      const wanted = normalizeLabel(value);
      const option = Array.from(select.options).find((o) => normalizeLabel(o.textContent || '') === wanted)
        || Array.from(select.options).find((o) => normalizeLabel(o.textContent || '').includes(wanted));
      if (option) {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        missed.push(label);
      }
    } else if (tag === 'INPUT' || tag === 'TEXTAREA') {
      const field = target as HTMLInputElement | HTMLTextAreaElement;
      let toSet = value.trim();
      // <input type=date> akzeptiert NUR exakt "JJJJ-MM-TT" (10 Zeichen) — ein
      // Zeitanteil wie "T00:00:00.000Z" oder sonstiger Anhang lässt den Setter
      // die gesamte Eingabe verwerfen. Nur das Datum selbst herausziehen.
      if ((field as HTMLInputElement).type === 'date') {
        const iso = toSet.match(/\d{4}-\d{2}-\d{2}/);
        if (iso) {
          toSet = iso[0];
        } else {
          // Ältere Kundendaten stehen teils noch im deutschen Format (TT.MM.JJJJ)
          // statt ISO — vor dem Setzen umwandeln, sonst verwirft der Browser alles.
          const de = toSet.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
          if (de) toSet = `${de[3]}-${de[2].padStart(2, '0')}-${de[1].padStart(2, '0')}`;
        }
      }
      setNativeValue(field, toSet);
      // <input type=date> etc. verwerfen ein nicht passendes Format lautlos (kein
      // Fehler, das Feld bleibt einfach leer) — Rückprobe macht das sichtbar, statt
      // dass „ausgefüllt" gemeldet wird, obwohl das Feld leer geblieben ist.
      if (!field.value) missed.push(label);
    } else {
      missed.push(label);
    }
  }
  return missed;
}

/**
 * Heuristische Erkennung der BAFA-Bestätigungsseite nach dem Absenden eines Antrags.
 * UNVERIFIZIERT (keine echte Bestätigungsseite als Referenz vorhanden) — bewusst breit
 * gehalten: Formular verschwindet (kaum noch Eingabefelder sichtbar) UND Seitentext enthält
 * ein Bestätigungs-Signalwort, ODER die URL wechselt auf einen BAFA-typischen Pfad.
 * Falsch-negativ = kein Risiko (bleibt beim manuellen Ist-Zustand). Falsch-positiv legt nur
 * ein zusätzliches, dedupliziertes customer_events-Event an. Bei Bedarf in einer künftigen
 * Session anhand einer echten Bestätigungsseite nachschärfen.
 */
export function detectSubmissionConfirmation(doc: Document): boolean {
  const url = doc.location?.href || '';
  if (/bestaetigung|confirm|quittung|abschluss|erfolg/i.test(url)) return true;
  const text = (doc.body?.innerText || doc.body?.textContent || '').toLowerCase();
  const signalwort = /(ihr\s+antrag|ihre\s+angaben).{0,120}(eingegangen|eingereicht|übermittelt|erfolgreich)|vorgangsnummer|antragsnummer/i.test(text);
  if (!signalwort) return false;
  const eingabefelder = doc.querySelectorAll('input:not([type=hidden]), select, textarea').length;
  return eingabefelder === 0;
}

/** Diagnose-Dump: welche Formularfelder/Labels/Select-Optionen kommen im iframe wirklich
 *  an — zum Abgleich der Label-Texte gegen die echten BAFA-Formulare. */
export function buildDiagnosticsText(doc: Document, fallbackUrl?: string): string {
  const lines: string[] = [];
  lines.push(`URL: ${doc.location?.href || fallbackUrl || ''}`);
  lines.push(`Titel: ${doc.title}`);
  const fields = Array.from(doc.querySelectorAll('input, select, textarea'));
  lines.push(`Formularfelder gefunden: ${fields.length}`);
  lines.push(`<label>-Elemente gefunden: ${doc.querySelectorAll('label').length}`);
  lines.push('');
  fields.forEach((el, i) => {
    const tag = el.tagName.toLowerCase();
    const type = (el as HTMLInputElement).type || '';
    const name = el.getAttribute('name') || '';
    const id = el.id || '';
    let labelText = '';
    if (id) {
      const lab = doc.querySelector(`label[for="${CSS.escape(id)}"]`);
      if (lab) labelText = lab.textContent?.trim() || '';
    }
    if (!labelText) {
      const closestLabel = el.closest('label');
      if (closestLabel) labelText = closestLabel.textContent?.trim() || '';
    }
    lines.push(`${i + 1}. <${tag}${type ? ' type=' + type : ''}> name="${name}" id="${id}" label="${labelText}"`);
    if (tag === 'select') {
      const opts = Array.from((el as HTMLSelectElement).options)
        .map((o) => `"${o.textContent?.trim()}"=${JSON.stringify(o.value)}`)
        .join(', ');
      lines.push(`   Optionen: ${opts}`);
    }
  });
  return lines.join('\n');
}
